import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';
import { usePagination, SearchBar } from '../components/Pagination';
import { LEVELS_BY_CYCLE } from '../utils/cycle.helpers';

const STATUS_ENUM = {
  ACTIVE:    ['green', 'Inscrit'],
  SUSPENDUE: ['gold',  'Suspendu'],
  TERMINEE:  ['gray',  'Terminé'],
};

const BLOOD_GROUPS   = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const ELECTROPHORESIS = ['AA', 'AS', 'SS', 'AC', 'SC'];
const RELATIONSHIPS  = ['Père', 'Mère', 'Tuteur', 'Tutrice', 'Grand-père', 'Grand-mère', 'Autre'];
const PROFESSIONS    = [
  'Agriculteur(e)', 'Artisan(e)', 'Avocat(e)', 'Chauffeur', 'Commerçant(e)',
  'Comptable', 'Directeur(rice)', 'Electricien(ne)', 'Enseignant(e)', 'Fonctionnaire',
  'Infirmier(ère)', 'Ingénieur(e)', 'Journaliste', 'Magistrat(e)', 'Médecin',
  'Militaire', 'Ouvrier(ère)', 'Pharmacien(ne)', 'Policier(ère)', 'Retraité(e)',
  'Sage-femme', 'Secrétaire', 'Technicien(ne)', 'Sans emploi', 'Autre',
];
const EMPTY_FORM = {
  matricule: '', firstName: '', lastName: '', gender: 'M',
  birthDate: '', birthPlace: '', motherTongue: '', originSchool: '',
  bloodGroup: '', phenotype: '',
  country: '', region: '', province: '',
};

const COUNTRIES = ['Burkina Faso', "Côte d'Ivoire", 'Mali', 'Sénégal', 'Ghana', 'Togo', 'Bénin', 'Niger', 'Guinée', 'Cameroun', 'Autre'];

const BF_REGIONS = [
  'Bankui', 'Djôrô', 'Guiriko', 'Goulmou', 'Kadiogo', 'Koulsé',
  'Liptako', 'Nakambé', 'Nando', 'Nazinon', 'Oubri', 'Sirba', 'Soum',
];

const BF_PROVINCES = [
  'Balé', 'Bam', 'Banwa', 'Bassitenga', 'Bazèga', 'Bougouriba', 'Boulgou',
  'Boulkiemdé', 'Comoé', 'Djelgodji', 'Dyamongou', 'Ganzourgou', 'Gnagna',
  'Godnangou', 'Gourma', 'Houet', 'Ioba', 'Kadiogo', 'Karo Peli', 'Kénédougou',
  'Komondjari', 'Kompienga', 'Koosin', 'Koulpéogo', 'Kourittenga', 'Kourweogo',
  'Leraba', 'Loroum', 'Mouhoun', 'Nahouri', 'Namentenga', 'Nayala', 'Noumbiel',
  'Oudalan', 'Passoré', 'Poni', 'Sandbondtenga', 'Sanguié', 'Séno', 'Sissili',
  'Sourou', 'Tannounyan', 'Tapoa', 'Tuy', 'Yaadga', 'Yagha', 'Ziro', 'Zondoma', 'Zoundweogo',
];

// Formatage montant
function fmt(n) { return new Intl.NumberFormat('fr-FR').format(n ?? 0) + ' F'; }

// Identifie la ligne Scolarité
function isScolarite(fee) { return fee.name?.toLowerCase().includes('scolarit'); }

export default function Admissions() {
  const [tab, setTab]           = useState('liste');
  const [enrollments, setEnrollments] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [periods, setPeriods]   = useState([]);
  const [year, setYear]         = useState(null);
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [flash, setFlash]       = useState('');
  const [modal, setModal]       = useState(null);

  function showFlash(msg) { setFlash(msg); setTimeout(() => setFlash(''), 3000); }

  async function load() {
    setLoading(true);
    try {
      const [cls, per, yr] = await Promise.all([
        api.get('/catalog/classes').catch(() => []),
        api.get('/catalog/periods').catch(() => []),
        api.get('/catalog/active-year').catch(() => null),
      ]);
      setClasses(cls || []);
      setPeriods(per || []);
      setYear(yr);
      const url = yr?.id ? `/enrollments?academicYearId=${yr.id}` : '/enrollments';
      const enr = await api.get(url).catch(() => []);
      setEnrollments(Array.isArray(enr) ? enr : []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const filtered = enrollments.filter((e) => {
    if (classFilter && e.classId !== classFilter) return false;
    if (statusFilter && e.status !== statusFilter) return false;
    return true;
  });

  const { paginated, search, setSearch, Pagination, total, filtered: pgFiltered } = usePagination(filtered, {
    pageSize: 5,
    searchKeys: ['student.lastName', 'student.firstName', 'student.matricule'],
  });

  const stats = {
    total:    enrollments.length,
    actifs:   enrollments.filter((e) => e.status === 'ACTIVE').length,
    suspendus:enrollments.filter((e) => e.status === 'SUSPENDUE').length,
    termines: enrollments.filter((e) => e.status === 'TERMINEE').length,
  };

  async function suspend(enrollment) {
    if (!(await confirmDialog({ title: 'Suspendre', message: `Suspendre l'inscription de ${enrollment.student?.lastName} ?`, danger: true }))) return;
    try {
      await api.patch(`/enrollments/${enrollment.id}`, { status: 'SUSPENDUE' });
      showFlash('Inscription suspendue.');
      load();
    } catch (e) { setError(e.message); }
  }

  async function reactivate(enrollment) {
    try {
      await api.patch(`/enrollments/${enrollment.id}`, { status: 'ACTIVE' });
      showFlash('Inscription réactivée ✓');
      load();
    } catch (e) { setError(e.message); }
  }

  async function deleteEnrollment(enrollment) {
    const name = `${enrollment.student?.lastName || ''} ${enrollment.student?.firstName || ''}`.trim();
    if (!(await confirmDialog({ title: 'Supprimer l\'inscription', message: `Supprimer définitivement l'inscription de ${name} ? Cette action est irréversible.`, confirmLabel: 'Supprimer', danger: true }))) return;
    try {
      // Essai 1 : route student sub-resource
      await api.del(`/students/${enrollment.studentId}/enrollments/${enrollment.id}`)
        // Essai 2 : DELETE direct
        .catch(() => api.del(`/enrollments/${enrollment.id}`))
        // Essai 3 : marquer TERMINEE si aucun DELETE n'existe
        .catch(() => api.patch(`/enrollments/${enrollment.id}`, { status: 'TERMINEE' }));
      showFlash('Inscription supprimée.');
      load();
    } catch (e) { setError(e.message); }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: '1.15rem', fontWeight: 700 }}>Admissions & Réinscriptions</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Gestion des inscriptions pour l'année {year?.label || '—'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost btn-sm" onClick={() => setModal('reinscription')}>
            🔄 Réinscription
          </button>
          <button className="btn btn-sm" onClick={() => setModal('new')}>
            + Nouvelle admission
          </button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {flash && <div className="receipt-banner">{flash}</div>}

      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
        <div className="kpi"><span>Total inscriptions</span><strong>{stats.total}</strong></div>
        <div className="kpi green"><span>Actifs</span><strong>{stats.actifs}</strong></div>
        <div className="kpi gold"><span>Suspendus</span><strong>{stats.suspendus}</strong></div>
        <div className="kpi"><span>Terminés</span><strong>{stats.termines}</strong></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'liste' ? 'active' : ''}`} onClick={() => setTab('liste')}>Liste des inscriptions</button>
        <button className={`tab ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>Statistiques par classe</button>
      </div>

      {tab === 'liste' && (
        <div className="card">
          <div className="card-head">
            <h2>Inscriptions {year?.label || ''}</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px' }}>
                <option value="">Toutes les classes</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px' }}>
                <option value="">Tous les statuts</option>
                <option value="ACTIVE">Actif</option>
                <option value="SUSPENDUE">Suspendu</option>
                <option value="TERMINEE">Terminé</option>
              </select>
            </div>
          </div>
          <SearchBar value={search} onChange={setSearch} placeholder="Nom, prénom, matricule…" total={total} filtered={pgFiltered.length} />
          <div className="panel-body">
            {loading ? <div className="loading">Chargement…</div> :
             pgFiltered.length === 0 ? <div className="empty">Aucune inscription trouvée.</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Élève</th><th>Classe</th><th>Cycle</th>
                      <th>Date d'inscription</th><th>Statut</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((e) => {
                      const [badgeClass, badgeLabel] = STATUS_ENUM[e.status] || ['gray', e.status];
                      const cls = classes.find((c) => c.id === e.classId);
                      const cycleName = cls?.level?.cycle?.name || '—';
                      return (
                        <tr key={e.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="avatar-initials">
                                {e.student?.firstName?.[0]}{e.student?.lastName?.[0]}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 12 }}>{e.student?.lastName} {e.student?.firstName}</div>
                                <div className="mono" style={{ fontSize: 10, color: 'var(--muted-light)' }}>{e.student?.matricule}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ fontSize: 12 }}>{cls?.name || '—'}</td>
                          <td><span className="badge gray" style={{ fontSize: 10 }}>{cycleName}</span></td>
                          <td className="mono" style={{ fontSize: 11 }}>
                            {e.enrollmentDate ? new Date(e.enrollmentDate).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td><span className={`badge ${badgeClass}`}>{badgeLabel}</span></td>
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              {e.status === 'ACTIVE' ? (
                                <button className="btn-ghost btn-sm" style={{ color: 'var(--warn)' }} onClick={() => suspend(e)}>Suspendre</button>
                              ) : e.status === 'SUSPENDUE' ? (
                                <button className="btn-ghost btn-sm" style={{ color: 'var(--success)' }} onClick={() => reactivate(e)}>Réactiver</button>
                              ) : null}
                              <button className="btn-ghost btn-sm" onClick={() => setModal(e)}>Modifier</button>
                              <button className="btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={() => deleteEnrollment(e)}>Supprimer</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Pagination />
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'stats' && (
        <div className="card">
          <div className="card-head"><h2>Effectifs par classe</h2></div>
          <div className="panel-body">
            {classes.length === 0 ? <div className="empty">Aucune classe.</div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Classe</th><th>Cycle</th><th className="num">Inscrits</th><th className="num">Actifs</th><th className="num">Suspendus</th><th style={{ width: 160 }}>Taux de remplissage</th></tr>
                  </thead>
                  <tbody>
                    {classes.map((cls) => {
                      const classEnr = enrollments.filter((e) => e.classId === cls.id);
                      const actifs = classEnr.filter((e) => e.status === 'ACTIVE').length;
                      const suspendus = classEnr.filter((e) => e.status === 'SUSPENDUE').length;
                      const capacity = cls.capacity || 40;
                      const taux = Math.min(100, Math.round(actifs / capacity * 100));
                      const cycleName = cls.level?.cycle?.name || '—';
                      return (
                        <tr key={cls.id}>
                          <td><b style={{ fontSize: 12 }}>{cls.name}</b></td>
                          <td><span className="badge gray" style={{ fontSize: 10 }}>{cycleName}</span></td>
                          <td className="num" style={{ fontWeight: 700 }}>{classEnr.length}</td>
                          <td className="num" style={{ color: 'var(--success)', fontWeight: 700 }}>{actifs}</td>
                          <td className="num" style={{ color: 'var(--warn)' }}>{suspendus}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 8, background: 'var(--line-soft)', borderRadius: 999, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${taux}%`, borderRadius: 999, background: taux >= 90 ? 'var(--danger)' : taux >= 70 ? 'var(--warn)' : 'var(--success)' }} />
                              </div>
                              <span style={{ fontSize: 11, minWidth: 32 }}>{taux}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {modal === 'new' && (
        <AdmissionModal classes={classes} year={year} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); showFlash('Élève admis avec succès ✓'); }} />
      )}
      {modal === 'reinscription' && (
        <ReinscriptionModal classes={classes} year={year} enrollments={enrollments} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); showFlash('Réinscription effectuée ✓'); }} />
      )}
      {modal && modal.id && (
        <EditEnrollmentModal enrollment={modal} classes={classes} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); showFlash('Inscription modifiée ✓'); }} />
      )}
    </>
  );
}

const DRAFTS_KEY = 'admission_drafts';
function getDrafts() { try { return JSON.parse(localStorage.getItem(DRAFTS_KEY) || '[]'); } catch { return []; } }
function saveDrafts(list) { try { localStorage.setItem(DRAFTS_KEY, JSON.stringify(list)); } catch {} }

// ── Composant réutilisable : section frais + bourse ──────────────────────────
function FraisSection({ classId, fees, selFeeIds, onToggle, bourseOn, setBourseOn, bourseTarget, setBourseTarget, bourseKind, setBourseKind, bourseAmt, setBourseAmt }) {
  const scolariteFee = fees.find(isScolarite);
  const optFees      = fees.filter((f) => !isScolarite(f));

  const scolariteAmt  = scolariteFee?.amount ?? 0;
  const optionalTotal = optFees.filter((f) => selFeeIds.has(f.id)).reduce((s, f) => s + (f.amount ?? 0), 0);
  const total         = scolariteAmt + optionalTotal;

  const bourseBase = bourseTarget === 'scolarite' ? scolariteAmt : total;
  const discount   = bourseOn && bourseAmt
    ? bourseKind === 'percent'
      ? Math.round(bourseBase * Math.min(100, Math.max(0, parseFloat(bourseAmt) || 0)) / 100)
      : Math.max(0, parseFloat(bourseAmt) || 0)
    : 0;
  const net = Math.max(0, total - discount);

  if (!classId || fees.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div className="fieldset-title">Frais de scolarité</div>

      {/* Scolarité — fixe et grisé */}
      {scolariteFee && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 16, height: 16, flexShrink: 0, borderRadius: 3, border: '2px solid var(--primary,#2563eb)', background: 'var(--primary,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>✓</span>
            </div>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{scolariteFee.name}</span>
            <span style={{ fontSize: 10, color: 'var(--muted)', background: 'var(--line-soft)', padding: '1px 8px', borderRadius: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>Obligatoire</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', background: 'var(--line-soft)', padding: '4px 12px', borderRadius: 6, minWidth: 110, textAlign: 'right', display: 'inline-block' }}>
            {fmt(scolariteFee.amount)}
          </span>
        </div>
      )}

      {/* Frais optionnels — cases à cocher */}
      {optFees.map((fee) => {
        const checked = selFeeIds.has(fee.id);
        return (
          <div key={fee.id}
            onClick={() => onToggle(fee.id)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: `1px solid ${checked ? 'var(--primary-light,#bfdbfe)' : 'var(--line-soft)'}`, borderRadius: 8, marginBottom: 6, cursor: 'pointer', background: checked ? 'var(--primary-soft,#eff6ff)' : 'transparent', transition: 'background .1s,border-color .1s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 16, height: 16, flexShrink: 0, borderRadius: 3, border: `2px solid ${checked ? 'var(--primary,#2563eb)' : '#9ca3af'}`, background: checked ? 'var(--primary,#2563eb)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {checked && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>✓</span>}
              </div>
              <span style={{ fontSize: 13 }}>{fee.name}</span>
            </div>
            <span style={{ fontWeight: checked ? 700 : 400, fontSize: 13, color: checked ? 'var(--text)' : 'var(--muted)' }}>
              {fmt(fee.amount)}
            </span>
          </div>
        );
      })}

      {/* Total sélectionné */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 8, marginTop: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Total sélectionné</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary,#2563eb)' }}>{fmt(total)}</span>
      </div>

      {/* Bourse */}
      <div style={{ marginTop: 14, border: '1px solid var(--line)', borderRadius: 8, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: bourseOn ? 14 : 0 }} onClick={() => setBourseOn((v) => !v)}>
          <div style={{ width: 16, height: 16, flexShrink: 0, borderRadius: 3, border: `2px solid ${bourseOn ? 'var(--primary,#2563eb)' : '#9ca3af'}`, background: bourseOn ? 'var(--primary,#2563eb)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {bourseOn && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Appliquer une bourse / réduction</span>
        </div>

        {bourseOn && (
          <>
            <div className="grid-3">
              <div className="field">
                <label>Appliquer sur</label>
                <select value={bourseTarget} onChange={(e) => setBourseTarget(e.target.value)}>
                  <option value="total">Total des frais</option>
                  <option value="scolarite">Scolarité uniquement</option>
                </select>
              </div>
              <div className="field">
                <label>Type de réduction</label>
                <select value={bourseKind} onChange={(e) => setBourseKind(e.target.value)}>
                  <option value="percent">Pourcentage (%)</option>
                  <option value="amount">Montant fixe (F)</option>
                </select>
              </div>
              <div className="field">
                <label>{bourseKind === 'percent' ? 'Taux (%)' : 'Montant (F)'}</label>
                <input type="number" min="0" max={bourseKind === 'percent' ? 100 : undefined}
                  value={bourseAmt} onChange={(e) => setBourseAmt(e.target.value)}
                  placeholder={bourseKind === 'percent' ? 'Ex : 50' : 'Ex : 25000'} />
              </div>
            </div>

            {bourseAmt && (
              <div style={{ background: 'var(--surface-soft)', border: '1px solid var(--line-soft)', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                  <span>Montant brut</span><span>{fmt(total)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--success,#16a34a)', marginBottom: 8 }}>
                  <span>
                    Bourse ({bourseTarget === 'scolarite' ? 'scolarité' : 'total'}
                    {bourseKind === 'percent' ? ` · ${bourseAmt}%` : ''})
                  </span>
                  <span>− {fmt(discount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                  <span>Net à payer</span>
                  <span style={{ color: 'var(--primary,#2563eb)' }}>{fmt(net)}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Modal Nouvelle Admission ──────────────────────────
function AdmissionModal({ classes, year, onClose, onSaved }) {
  const [drafts, setDrafts]       = useState(getDrafts);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [step, setStep]           = useState(() => getDrafts().length > 0 ? 0 : 'form');
  const [activeTab, setActiveTab] = useState('identite');
  const [f, setF]                 = useState({ ...EMPTY_FORM });
  const [parents, setParents]     = useState([{ firstName: '', lastName: '', relationship: 'Père', phone: '', whatsapp: '', email: '', profession: '', isPrimary: true }]);
  const [enroll, setEnroll]       = useState({ classId: classes[0]?.id || '', enrollmentDate: new Date().toISOString().slice(0, 10) });
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);

  // — Frais —
  const [classFees, setClassFees]       = useState([]);
  const [selFeeIds, setSelFeeIds]       = useState(new Set());
  const [bourseOn, setBourseOn]         = useState(false);
  const [bourseTarget, setBourseTarget] = useState('total');
  const [bourseKind, setBourseKind]     = useState('percent');
  const [bourseAmt, setBourseAmt]       = useState('');

  useEffect(() => {
    const cid = enroll.classId;
    if (!cid) { setClassFees([]); setSelFeeIds(new Set()); return; }
    api.get(`/finance/fee-types?classId=${cid}`)
      .catch(() => api.get('/finance/fee-types'))
      .then((d) => { setClassFees(Array.isArray(d) ? d : []); setSelFeeIds(new Set()); })
      .catch(() => setClassFees([]));
  }, [enroll.classId]);

  function toggleFee(id) {
    setSelFeeIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  useEffect(() => {
    api.get('/establishment').then((school) => {
      if (school?.country) setF((p) => ({ ...p, country: school.country }));
    }).catch(() => {});
  }, []);

  const set  = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setE = (k) => (e) => setEnroll((p) => ({ ...p, [k]: e.target.value }));

  function setParent(i, k, v) { setParents((prev) => prev.map((p, j) => j === i ? { ...p, [k]: v } : p)); }
  function addParent()        { setParents((prev) => [...prev, { firstName: '', lastName: '', relationship: 'Mère', phone: '', whatsapp: '', email: '', profession: '', isPrimary: false }]); }
  function removeParent(i)    { setParents((prev) => prev.filter((_, j) => j !== i)); }

  function loadDraft(d) {
    setActiveDraftId(d.id);
    setActiveTab(d.activeTab || 'identite');
    setF(d.f || { ...EMPTY_FORM });
    setParents(d.parents || [{ firstName: '', lastName: '', relationship: 'Père', phone: '', whatsapp: '', email: '', profession: '', isPrimary: true }]);
    setEnroll(d.enroll || { classId: classes[0]?.id || '', enrollmentDate: new Date().toISOString().slice(0, 10) });
    setStep('form');
  }

  function startNew() {
    setActiveDraftId(null);
    setActiveTab('identite');
    setF({ ...EMPTY_FORM });
    setParents([{ firstName: '', lastName: '', relationship: 'Père', phone: '', whatsapp: '', email: '', profession: '', isPrimary: true }]);
    setEnroll({ classId: classes[0]?.id || '', enrollmentDate: new Date().toISOString().slice(0, 10) });
    setStep('form');
  }

  function pause() {
    const id = activeDraftId || crypto.randomUUID();
    const current = getDrafts();
    const updated = current.filter((d) => d.id !== id).concat({ id, savedAt: new Date().toISOString(), activeTab, f, parents, enroll });
    saveDrafts(updated);
    onClose();
  }

  function deleteDraft(id) {
    const updated = getDrafts().filter((d) => d.id !== id);
    saveDrafts(updated);
    setDrafts(updated);
    if (updated.length === 0) startNew();
  }

  function removeDraftAndProceed(id) { saveDrafts(getDrafts().filter((d) => d.id !== id)); }

  async function save() {
    if (!f.firstName || !f.lastName) { setError('Nom et prénom sont obligatoires.'); setActiveTab('identite'); return; }
    if (!enroll.classId) { setError('Veuillez sélectionner une classe.'); setActiveTab('scolarite'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        matricule:    f.matricule    || undefined,
        firstName:    f.firstName,
        lastName:     f.lastName,
        gender:       f.gender,
        birthDate:    f.birthDate    || undefined,
        birthPlace:   f.birthPlace   || undefined,
        bloodGroup:   f.bloodGroup   || undefined,
        phenotype:    f.phenotype    || undefined,
        motherTongue: f.motherTongue || undefined,
        originSchool: f.originSchool || undefined,
      };
      const created = await api.post('/students', payload);
      await api.post(`/students/${created.id}/enroll`, {
        classId: enroll.classId,
        academicYearId: year?.id,
      });

      // Génère uniquement les factures pour les frais sélectionnés
      try {
        const scFee   = classFees.find(isScolarite);
        const feeIds  = classFees.length > 0
          ? [...(scFee ? [scFee.id] : []), ...Array.from(selFeeIds)]
          : (await api.get('/finance/fee-types').catch(() => [])).map((f) => f.id);
        for (const feeTypeId of feeIds) {
          await api.post('/finance/invoices/generate', { classId: enroll.classId, feeTypeId }).catch(() => {});
        }
      } catch (_) {}

      for (const p of parents) {
        if (p.firstName && p.lastName) {
          const { _parentId, ...data } = p;
          await api.post(`/students/${created.id}/parents`, data);
        }
      }
      if (activeDraftId) removeDraftAndProceed(activeDraftId);
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  const TABS = [['identite', 'Identité'], ['scolarite', 'Scolarité'], ['parents', 'Parents & tuteurs']];

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>
            {step === 0 ? 'Brouillons en attente' : 'Nouvelle admission'}
          </h2>
        </div>

        {step === 0 && (
          <>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
              {drafts.length} brouillon{drafts.length > 1 ? 's' : ''} en attente — choisissez-en un ou démarrez une nouvelle admission.
            </div>
            {drafts.map((d) => {
              const name = [d.f?.lastName, d.f?.firstName].filter(Boolean).join(' ') || '(sans nom)';
              const tabLabel = { identite: 'Identité', scolarite: 'Scolarité', parents: 'Parents & tuteurs' }[d.activeTab] || '—';
              const date = new Date(d.savedAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 8, marginBottom: 8, background: 'var(--surface-soft)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>⏸ {name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Onglet : {tabLabel} · {date}</div>
                  </div>
                  <button className="btn btn-sm" style={{ width: 'auto', padding: '0 14px' }} onClick={() => loadDraft(d)}>Reprendre</button>
                  <button className="link-danger" style={{ fontSize: 11 }} onClick={() => deleteDraft(d.id)}>Supprimer</button>
                </div>
              );
            })}
            <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
              <button className="btn" onClick={startNew} style={{ width: 'auto', padding: '0 22px' }}>+ Nouvelle admission</button>
              <button className="btn-ghost" onClick={onClose}>Annuler</button>
            </div>
          </>
        )}

        {step === 'form' && (
          <>
            {error && <div className="form-error">{error}</div>}
            <div className="tabs" style={{ marginBottom: 12 }}>
              {TABS.map(([v, l]) => (
                <button key={v} className={`tab ${activeTab === v ? 'active' : ''}`} onClick={() => { setError(''); setActiveTab(v); }}>{l}</button>
              ))}
            </div>

            {activeTab === 'identite' && (
              <>
                <div className="fieldset-title">Identification</div>
                <div className="grid-3">
                  <div className="field"><label>Matricule</label><input value={f.matricule} onChange={set('matricule')} placeholder="Auto-généré à la validation" /></div>
                  <div className="field"><label>Nom *</label><input value={f.lastName} onChange={set('lastName')} /></div>
                  <div className="field"><label>Prénom(s) *</label><input value={f.firstName} onChange={set('firstName')} /></div>
                  <div className="field"><label>Sexe *</label>
                    <select value={f.gender} onChange={set('gender')}>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  </div>
                  <div className="field"><label>Date de naissance</label><input type="date" value={f.birthDate} onChange={set('birthDate')} /></div>
                  <div className="field"><label>Lieu de naissance</label><input value={f.birthPlace} onChange={set('birthPlace')} /></div>
                </div>
                <div className="fieldset-title">Informations médicales</div>
                <div className="grid-3">
                  <div className="field"><label>Groupe sanguin</label>
                    <select value={f.bloodGroup} onChange={set('bloodGroup')}>
                      <option value="">— Inconnu —</option>
                      {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Électrophorèse</label>
                    <select value={f.phenotype} onChange={set('phenotype')}>
                      <option value="">—</option>
                      {ELECTROPHORESIS.map((e) => <option key={e}>{e}</option>)}
                    </select>
                  </div>
                </div>
                <div className="fieldset-title">Origine</div>
                <div className="grid-3">
                  <div className="field"><label>Langue maternelle</label><input value={f.motherTongue} onChange={set('motherTongue')} /></div>
                  <div className="field"><label>École d'origine</label><input value={f.originSchool} onChange={set('originSchool')} placeholder="Transfert depuis…" /></div>
                  <div className="field"><label>Pays</label>
                    <select value={f.country} onChange={(e) => setF((p) => ({ ...p, country: e.target.value, region: '', province: '' }))}>
                      <option value="">—</option>
                      {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  {f.country === 'Burkina Faso' && (
                    <div className="field"><label>Région</label>
                      <select value={f.region} onChange={set('region')}>
                        <option value="">—</option>
                        {BF_REGIONS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                  )}
                  {f.country === 'Burkina Faso' && (
                    <div className="field"><label>Province</label>
                      <select value={f.province} onChange={set('province')}>
                        <option value="">—</option>
                        {BF_PROVINCES.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'scolarite' && (
              <>
                <div className="fieldset-title">Inscription — Année {year?.label}</div>
                <div className="grid-3">
                  <div className="field"><label>Classe *</label>
                    <select value={enroll.classId} onChange={setE('classId')}>
                      <option value="">—</option>
                      {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Date d'inscription</label>
                    <input type="date" value={enroll.enrollmentDate} onChange={setE('enrollmentDate')} />
                  </div>
                </div>
                <FraisSection
                  classId={enroll.classId}
                  fees={classFees}
                  selFeeIds={selFeeIds}
                  onToggle={toggleFee}
                  bourseOn={bourseOn}     setBourseOn={setBourseOn}
                  bourseTarget={bourseTarget} setBourseTarget={setBourseTarget}
                  bourseKind={bourseKind} setBourseKind={setBourseKind}
                  bourseAmt={bourseAmt}   setBourseAmt={setBourseAmt}
                />
              </>
            )}

            {activeTab === 'parents' && (
              <>
                {parents.map((p, i) => (
                  <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <b>Parent / Tuteur {i + 1}</b>
                      {parents.length > 1 && <button className="link-danger" style={{ fontSize: '0.75rem' }} onClick={() => removeParent(i)}>Supprimer</button>}
                    </div>
                    <div className="grid-3">
                      <div className="field"><label>Nom *</label><input value={p.lastName} onChange={(e) => setParent(i, 'lastName', e.target.value)} /></div>
                      <div className="field"><label>Prénom *</label><input value={p.firstName} onChange={(e) => setParent(i, 'firstName', e.target.value)} /></div>
                      <div className="field"><label>Lien</label>
                        <select value={p.relationship} onChange={(e) => setParent(i, 'relationship', e.target.value)}>
                          {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                      <div className="field"><label>Téléphone</label><input value={p.phone} onChange={(e) => setParent(i, 'phone', e.target.value)} /></div>
                      <div className="field"><label>WhatsApp</label><input value={p.whatsapp} onChange={(e) => setParent(i, 'whatsapp', e.target.value)} /></div>
                      <div className="field"><label>Email</label><input type="email" value={p.email} onChange={(e) => setParent(i, 'email', e.target.value)} /></div>
                      <div className="field"><label>Profession</label>
                        <select value={p.profession} onChange={(e) => setParent(i, 'profession', e.target.value)}>
                          <option value="">—</option>
                          {PROFESSIONS.map((pr) => <option key={pr}>{pr}</option>)}
                        </select>
                      </div>
                      <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
                        <input type="radio" name="tuteurPrincipal" checked={!!p.isPrimary} onChange={() => setParents((prev) => prev.map((pp, j) => ({ ...pp, isPrimary: j === i })))} id={`primary-${i}`} />
                        <label htmlFor={`primary-${i}`} style={{ fontWeight: p.isPrimary ? 700 : 400, cursor: 'pointer' }}>Tuteur principal</label>
                      </div>
                    </div>
                  </div>
                ))}
                <button className="btn-ghost btn-sm" onClick={addParent}>+ Ajouter un parent / tuteur</button>
              </>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
                {saving ? 'Enregistrement…' : '✓ Confirmer l\'admission'}
              </button>
              <button className="btn-ghost" onClick={pause} title="Sauvegarder et reprendre plus tard">⏸ Mettre en pause</button>
              <button className="btn-ghost" onClick={onClose}>Annuler</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Filtre de classes de destination pour la réinscription ───
const ALL_LEVELS = [
  ...LEVELS_BY_CYCLE.PRESCOLAIRE,
  ...LEVELS_BY_CYCLE.ELEMENTAIRE,
  ...LEVELS_BY_CYCLE.POST_ELEMENTAIRE,
  ...LEVELS_BY_CYCLE.SECONDAIRE,
];

function matchLevel(className) {
  const upper = (className || '').toUpperCase();
  return ALL_LEVELS
    .filter((l) => upper.includes(l.code.toUpperCase()))
    .sort((a, b) => b.code.length - a.code.length)[0] || null;
}

function sortByAcademicOrder(classList) {
  return [...classList].sort((a, b) => {
    const ia = ALL_LEVELS.findIndex((l) => l.code === matchLevel(a.name)?.code);
    const ib = ALL_LEVELS.findIndex((l) => l.code === matchLevel(b.name)?.code);
    if (ia !== ib) return ia - ib;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function getDestinationClasses(classes, currentClassName, decision) {
  if (!currentClassName || currentClassName === '—') return sortByAcademicOrder(classes);
  const cur = matchLevel(currentClassName);
  if (!cur) return sortByAcademicOrder(classes);
  const curIdx = ALL_LEVELS.findIndex((l) => l.code === cur.code);
  if (curIdx === -1) return sortByAcademicOrder(classes);
  if (decision === 'REDOUBLE') {
    const same = classes.filter((c) => matchLevel(c.name)?.code === cur.code);
    return sortByAcademicOrder(same.length > 0 ? same : classes);
  }
  // ADMIS : les 2 niveaux immédiatement supérieurs
  const nextCodes = ALL_LEVELS.slice(curIdx + 1, curIdx + 3).map((l) => l.code);
  if (nextCodes.length === 0) return sortByAcademicOrder(classes);
  const filtered = classes.filter((c) => nextCodes.includes(matchLevel(c.name)?.code));
  return sortByAcademicOrder(filtered.length > 0 ? filtered : classes);
}

// ── Modal Réinscription ───────────────────────────────
function ReinscriptionModal({ classes, year, enrollments, onClose, onSaved }) {
  const [allStudents, setAllStudents] = useState([]);
  const [prevEnrMap, setPrevEnrMap]   = useState({});
  const [loading, setLoading]         = useState(true);
  const [classFilter, setClassFilter] = useState('');

  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState('ADMIS');
  const [toClass, setToClass]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState([]);

  const [classFees, setClassFees]       = useState([]);
  const [selFeeIds, setSelFeeIds]       = useState(new Set());
  const [bourseOn, setBourseOn]         = useState(false);
  const [bourseTarget, setBourseTarget] = useState('total');
  const [bourseKind, setBourseKind]     = useState('percent');
  const [bourseAmt, setBourseAmt]       = useState('');

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [students, years] = await Promise.all([
          api.get('/students').catch(() => []),
          api.get('/catalog/years').catch(() => []),
        ]);
        setAllStudents(Array.isArray(students) ? students : []);

        // Charger les inscriptions de l'année précédente pour afficher "Dernière Classe"
        const sortedYears = (Array.isArray(years) ? years : [])
          .slice().sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        const currentIdx = sortedYears.findIndex((y) => y.id === year?.id);
        const prevYear = sortedYears[currentIdx + 1] || sortedYears.find((y) => y.id !== year?.id);
        if (prevYear) {
          const prevEnr = await api.get(`/enrollments?academicYearId=${prevYear.id}`).catch(() => []);
          const map = {};
          (Array.isArray(prevEnr) ? prevEnr : []).forEach((e) => {
            if (e.studentId && !map[e.studentId]) map[e.studentId] = e.class?.name || e.className || null;
          });
          setPrevEnrMap(map);
        }
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (!toClass) { setClassFees([]); setSelFeeIds(new Set()); return; }
    api.get(`/finance/fee-types?classId=${toClass}`)
      .then((d) => {
        const arr = Array.isArray(d) ? d : [];
        return arr.length > 0 ? arr : api.get('/finance/fee-types').catch(() => []);
      })
      .catch(() => api.get('/finance/fee-types').catch(() => []))
      .then((d) => { setClassFees(Array.isArray(d) ? d : []); setSelFeeIds(new Set()); })
      .catch(() => setClassFees([]));
  }, [toClass]);

  function toggleFee(id) {
    setSelFeeIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const enrolledIds = new Set(enrollments.map((e) => e.studentId));
  const doneIds     = new Set(done.map((d) => d.studentId));

  function studentClassId(s) {
    const prevName = prevEnrMap[s.id];
    if (prevName) {
      const found = classes.find((c) => c.name === prevName);
      if (found) return found.id;
    }
    return s.enrollments?.[0]?.class?.id || s.classId || s.class?.id || s.enrollment?.classId || null;
  }

  const notEnrolled = allStudents
    .filter((s) => !enrolledIds.has(s.id) && !doneIds.has(s.id))
    .filter((s) => !classFilter || studentClassId(s) === classFilter);

  function prevClassOf(s) {
    return prevEnrMap[s.id] || s.enrollments?.[0]?.class?.name || classes.find((c) => c.id === studentClassId(s))?.name || '—';
  }

  function selectStudent(s) {
    setSelected(s);
    setDecision('ADMIS');
    setToClass('');
    setError('');
    setClassFees([]);
    setSelFeeIds(new Set());
    setBourseOn(false);
    setBourseAmt('');
  }

  function reset() {
    setSelected(null);
    setToClass('');
    setError('');
  }

  async function save() {
    if (!toClass) { setError('Sélectionnez la classe de destination.'); return; }
    setSaving(true); setError('');
    try {
      await api.post(`/students/${selected.id}/enroll`, {
        classId: toClass,
        academicYearId: year?.id,
      });
      try {
        const scFee  = classFees.find(isScolarite);
        const feeIds = classFees.length > 0
          ? [...(scFee ? [scFee.id] : []), ...Array.from(selFeeIds)]
          : [];
        for (const feeTypeId of feeIds) {
          await api.post('/finance/invoices/generate', { classId: toClass, feeTypeId }).catch(() => {});
        }
      } catch (_) {}
      setDone((d) => [...d, { studentId: selected.id, name: `${selected.lastName} ${selected.firstName}`, decision }]);
      reset();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>
            {selected ? `Réinscription — ${selected.lastName} ${selected.firstName}` : 'Réinscription'}
          </h2>
          {done.length > 0 && !selected && (
            <button className="btn btn-sm" onClick={onSaved}>
              Terminer ({done.length} traité{done.length > 1 ? 's' : ''})
            </button>
          )}
        </div>

        {/* Vue liste */}
        {!selected && (
          <>
            <div style={{ marginBottom: 12 }}>
              <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
                style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px', minWidth: 220 }}>
                <option value="">Toutes les classes</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="loading">Chargement…</div>
            ) : notEnrolled.length === 0 ? (
              <div className="empty">
                {classFilter ? 'Aucun élève non réinscrit dans cette classe.' : 'Tous les élèves sont déjà réinscrits.'}
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Élève</th>
                      <th>Matricule</th>
                      <th>Dernière classe</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {notEnrolled.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="avatar-initials">{s.firstName?.[0]}{s.lastName?.[0]}</div>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>{s.lastName} {s.firstName}</div>
                          </div>
                        </td>
                        <td className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>{s.matricule || '—'}</td>
                        <td style={{ fontSize: 12 }}>{prevClassOf(s)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm" style={{ width: 'auto', padding: '0 14px' }}
                            onClick={() => selectStudent(s)}>
                            Réinscrire
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {done.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                  Traités cette session ({done.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {done.map((d, i) => (
                    <span key={i} className={`badge ${d.decision === 'ADMIS' ? 'green' : 'gold'}`} style={{ fontSize: 10 }}>
                      {d.decision === 'ADMIS' ? '✓' : '↩'} {d.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <button className="btn-ghost btn-sm" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}

        {/* Vue formulaire */}
        {selected && (
          <>
            <div style={{ background: 'var(--surface-soft)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                  {(selected.firstName?.[0] || '') + (selected.lastName?.[0] || '')}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{selected.lastName} {selected.firstName}</div>
                  <div className="mono" style={{ color: 'var(--muted)', fontSize: 11 }}>
                    {selected.matricule} · Dernière classe : {prevClassOf(selected)}
                  </div>
                </div>
              </div>
            </div>

            <div className="field">
              <label>Décision pédagogique</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setDecision('ADMIS'); setToClass(''); }}
                  style={{ flex: 1, padding: '9px 0', border: `2px solid ${decision === 'ADMIS' ? 'var(--success)' : 'var(--line)'}`, borderRadius: 8, background: decision === 'ADMIS' ? 'var(--success-soft,#f0fdf4)' : 'transparent', color: decision === 'ADMIS' ? 'var(--success)' : 'var(--muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  ✓ Admis en classe supérieure
                </button>
                <button onClick={() => { setDecision('REDOUBLE'); setToClass(studentClassId(selected) || ''); }}
                  style={{ flex: 1, padding: '9px 0', border: `2px solid ${decision === 'REDOUBLE' ? 'var(--warn)' : 'var(--line)'}`, borderRadius: 8, background: decision === 'REDOUBLE' ? '#fffbeb' : 'transparent', color: decision === 'REDOUBLE' ? 'var(--warn)' : 'var(--muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  ↩ Redoublement
                </button>
              </div>
            </div>

            <div className="field">
              <label>Classe de destination — Année {year?.label || ''}</label>
              <select value={toClass} onChange={(e) => setToClass(e.target.value)}>
                <option value="">— Sélectionner —</option>
                {getDestinationClasses(classes, prevClassOf(selected), decision).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.id === studentClassId(selected) ? ' (même classe)' : ''}</option>
                ))}
              </select>
            </div>

            <FraisSection
              classId={toClass}
              fees={classFees}
              selFeeIds={selFeeIds}
              onToggle={toggleFee}
              bourseOn={bourseOn}         setBourseOn={setBourseOn}
              bourseTarget={bourseTarget} setBourseTarget={setBourseTarget}
              bourseKind={bourseKind}     setBourseKind={setBourseKind}
              bourseAmt={bourseAmt}       setBourseAmt={setBourseAmt}
            />

            {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn btn-sm" onClick={save} disabled={saving || !toClass} style={{ width: 'auto', padding: '0 22px' }}>
                {saving ? 'Enregistrement…' : '✓ Confirmer'}
              </button>
              <button className="btn-ghost btn-sm" onClick={reset}>← Retour à la liste</button>
              <button className="btn-ghost btn-sm" onClick={onClose}>Annuler</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Modal Modifier Inscription ────────────────────────
function EditEnrollmentModal({ enrollment, classes, onClose, onSaved }) {
  const [classId, setClassId]   = useState(enrollment.classId);
  const [status, setStatus]     = useState(enrollment.status);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  async function save() {
    setSaving(true); setError('');
    try {
      await api.patch(`/enrollments/${enrollment.id}`, { classId, status });
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 4, fontSize: '1rem', fontWeight: 700 }}>Modifier l'inscription</h2>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          {enrollment.student?.lastName} {enrollment.student?.firstName}
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="field"><label>Classe</label>
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Statut</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ACTIVE">Actif</option>
            <option value="SUSPENDUE">Suspendu</option>
            <option value="TERMINEE">Terminé</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
